import React from 'react';
import { useLingui } from '@lingui/react/macro';

import SimplifiedPriceColumn from '@rover/components/src/cards/SearchResultCard/PriceColumn/SimplifiedPriceColumn';
import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Button } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { SitterProfileVersionOptions } from '../../constants';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import AdditionalRatesSection from '../ServiceSection/AdditionalRatesSection';

import { SitterProfileSeeAllRatesClicked } from './analyticsEvents';
import { AWAY_TEXT_COLOR, isServiceAway } from './awayService';
import { CTAPropsCommon } from './RevampServicesCta';

type Props = {
  handleOpenSheet: () => void;
};

const RevampMobileCtaBarCommon = ({
  handleOpenSheet,
  priceTransparencyLevel,
  locale,
  petCount,
  petType,
}: Props & Omit<CTAPropsCommon, 'bookingFeePercentage'>): JSX.Element => {
  const { t } = useLingui();
  const {
    sitterData,
    serviceType,
    selectedService,
    serviceDisplayName: selectedServiceName,
    isBrightHorizonsUser,
    isSelectedServiceUnapproved,
  } = useRevampCta();
  const { rate, price } = useRevampCtaCommon(
    priceTransparencyLevel,
    petType,
    petCount,
    selectedService
  );

  const { showSittersRates } = useRevampProfileContext();
  const priceInCredits = rate?.priceInCredits != null ? Number(rate.priceInCredits) : null;
  const shouldShowPriceInCredits =
    isBrightHorizonsUser && !showSittersRates && priceInCredits != null;

  const isAway = isServiceAway(selectedService);
  const awayText = t`Away`;

  const serviceDisplayName =
    isAway && selectedServiceName ? `${selectedServiceName} (${awayText})` : selectedServiceName;

  return (
    <>
      <Button
        variant="link"
        size="default"
        icon={DropdownSmall}
        iconAlignment="right"
        onPress={handleOpenSheet}
        aria-label={
          serviceDisplayName ? t`Select a service: ${serviceDisplayName}` : t`Select a service`
        }
        padding="0x"
        sx={{
          color: isAway ? `text.${AWAY_TEXT_COLOR}` : 'text.primary',
        }}
      >
        {selectedServiceName ?? t`Select a service`}
      </Button>

      {isSelectedServiceUnapproved && (
        <Badge
          boxProps={{ testID: 'cta-coming-soon-badge' }}
          text={t`Coming Soon`}
          variant="default"
        />
      )}

      {!isSelectedServiceUnapproved && rate && price && (
        <AdditionalRatesSection
          services={sitterData.services}
          petType={petType}
          petCount={petCount}
          showSittersRates={showSittersRates}
          isCtaModal
          isRevampVersion
          renderTrigger={(toggleModal) => (
            <SimplifiedPriceColumn
              variant="cta-mobile"
              currencyCode={rate.pricePerUnitObj.currencyIso}
              priceTransparencyLevel={priceTransparencyLevel}
              locale={locale}
              price={price}
              priceInCredits={priceInCredits}
              shouldShowPriceInCredits={shouldShowPriceInCredits}
              showFromText={!shouldShowPriceInCredits}
              unitAsSentence={rate.unitAsSentence}
              isBHUser={isBrightHorizonsUser}
              onPriceClick={() => {
                emitAnalyticsEvent(
                  new SitterProfileSeeAllRatesClicked({
                    serviceSlug: serviceType ?? '',
                    pageVariant: SitterProfileVersionOptions.REVAMP,
                  })
                );
                toggleModal();
              }}
            />
          )}
        />
      )}
    </>
  );
};

export default RevampMobileCtaBarCommon;
